/**
 * pages/Search.js — Claim search and submission interface
 * =========================================================
 * Allows authenticated users to search for existing fact-checked claims
 * and submit new ones for review.
 *
 * Layout
 * -------
 * Left panel  — TextArea + Search button + Submit button (+ countdown timer)
 * Right panel — Results list (InfiniteScroll) showing similar posts
 *
 * Search flow
 * -----------
 * 1. User types a claim in the TextArea.
 * 2. Clicks Search → apiClient.searchPosts() is called (public, no auth).
 * 3. Results are displayed in the right panel.
 * 4. BLANK posts are blurred to prevent users from seeing unverified content;
 *    REAL/FAKE/SPAM tags are clickable and open a read-only verdict modal.
 *
 * Submit flow
 * -----------
 * 1. Submit button is disabled if:
 *    - No token (not logged in)
 *    - Geolocation not yet granted (location required for analytics)
 *    - ADC cooldown timer is active (user submitted recently)
 * 2. On click: checks if the exact message already exists in results.
 *    If found, rejects with an error message.
 * 3. Calls apiClient.submitPost() → POST /session/submit.
 * 4. Response handling:
 *    - SPAM result  → "Can't submit, already marked as spam" error.
 *    - adc_ts field → cooldown timer starts; button shows remaining time.
 *    - Otherwise    → success message, search results refreshed.
 *
 * ADC countdown timer
 * --------------------
 * `adc_ts` is an ISO timestamp returned by the backend indicating when
 * the user is next allowed to submit.  A 1-second setInterval updates
 * the button label in real time showing "Next Submit In: Xd Xh Xm Xs".
 * When the timer expires the button reverts to the normal submit state.
 *
 * API calls
 * ----------
 * apiClient.searchPosts(msg)       — POST /search          (public)
 * apiClient.readPost(addr)         — POST /read            (public, for modal)
 * apiClient.submitPost(msg, loc)   — POST /session/submit  (authenticated)
 */

import { useState, useEffect } from "react";
import apiClient from "../utils/apiClient";
import { Input, Row, Col, Button, List, message, Modal, Tag, Typography, Space, Upload, Spin, Select } from "antd";
import { SearchOutlined, SendOutlined, PlusOutlined, EnvironmentOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import "./components/markdown.css";
import InfiniteScroll from "react-infinite-scroll-component";
import { useLoc, useUsr } from "../App";
import Markdown from "react-markdown";
import SparkMD5 from "spark-md5";

const { TextArea } = Input;
const { Text } = Typography;

// Ant Design tag colour map for post labels
const LBL_COLOR = { BLANK: "blue", REAL: "success", FAKE: "error", SPAM: "warning" };

// XHR-based PUT upload — allows onProgress callbacks that fetch() cannot provide
const xhrUpload = (url, body, contentType, onProgress) =>
    new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = e => { if (e.lengthComputable) onProgress(Math.round(e.loaded / e.total * 100)); };
        xhr.onload  = resolve;
        xhr.onerror = () => reject(new Error("Upload failed"));
        xhr.open("PUT", url);
        xhr.setRequestHeader("Content-Type", contentType);
        xhr.send(body);
    });

// Modal appearance config per label — maps to Ant Design Modal[method]()
const MODAL_CFG = {
    FAKE: { method: "warning", title: "Fact Checked : Fake News" },
    REAL: { method: "success", title: "Fact Checked : Real News" },
    SPAM: { method: "error", title: "Fact Checked : Spam Post" },
    BLANK: { method: "info", title: "Yet to be Fact Checked" },
};

/**
 * getCountdown — formats the time remaining until a target timestamp.
 * Returns a human-readable countdown string used as the submit button label,
 * or the ready-to-submit message when the cooldown has elapsed.
 *
 * @param {string|null} targetTs — ISO timestamp from the `adc_ts` field.
 * @returns {string}
 */
function getCountdown(targetTs) {
    if (!targetTs) return "Submit";
    const now = Date.now();
    const diff = Date.parse(targetTs) - now;
    if (!diff || diff <= 0) return "Feel free to Submit, if not Found";
    const msPerSecond = 1000;
    const msPerMinute = msPerSecond * 60;
    const msPerHour = msPerMinute * 60;
    const msPerDay = msPerHour * 24;
    const days = Math.floor(diff / msPerDay);
    const hours = Math.floor((diff % msPerDay) / msPerHour);
    const minutes = Math.floor((diff % msPerHour) / msPerMinute);
    const seconds = Math.floor((diff % msPerMinute) / msPerSecond);
    return "Next Submit In: " + days + "d " + hours + "h " + minutes + "m " + seconds + "s";
}

export const Search = () => {
    const { usr, setUsr } = useUsr();
    const { loc } = useLoc();

    const [querystr, setQuerystr] = useState(() => localStorage.getItem('fnma_search_query') || '');  // persisted for session
    const [queryres, setQueryres] = useState([]);       // search results list
    const [searching, setSearching] = useState(false);

    const [selectedState, setSelectedState] = useState(() => {
        const raw = localStorage.getItem('fnma_search_state');
        if (raw === null) return usr.state;
        try { return JSON.parse(raw); } catch { return usr.state; }
    });
    const [channelStats, setChannelStats] = useState(null);
    const [messageApi, messageHolder] = message.useMessage();
    const [modalApi, modalHolder] = Modal.useModal();

    // ADC cooldown button display state
    const [buttonText, setButtonText] = useState("Submit");
    const [buttonDisabled, setButtonDisabled] = useState(false);

    // Claim attachments — restored from localStorage on mount; only done uploads are persisted.
    const [fileList, setFileList] = useState(() => {
        try { return JSON.parse(localStorage.getItem('fnma_search_files')) || []; }
        catch { return []; }
    });

    // Sync completed uploads to localStorage so they survive page navigation.
    // In-progress and error entries are intentionally excluded.
    useEffect(() => {
        const done = fileList
            .filter(f => f.status === 'done')
            .map(({ uid, name, url, thumbUrl, status, percent }) => ({ uid, name, url, thumbUrl, status, percent }));
        localStorage.setItem('fnma_search_files', JSON.stringify(done));
    }, [fileList]);

    // Fetch channel eligibility stats whenever the selected state changes.
    useEffect(() => {
        if (!selectedState || !usr.token) { setChannelStats(null); return; }
        apiClient.getChannelStats(selectedState)
            .then(r => setChannelStats(r.data))
            .catch(() => setChannelStats(null));
    }, [selectedState, usr.token]);

    const computeFileMD5 = async (file) => {
        const nativeFile = file.originFileObj || file;
        const arrayBuffer = await nativeFile.arrayBuffer();
        return SparkMD5.ArrayBuffer.hash(arrayBuffer);
    };

    const handleUpload = async (file) => {
        const fileObj = file.file;
        const isVideo = fileObj.type.startsWith("video/");
        setFileList(prev => [...prev, { uid: fileObj.uid, name: fileObj.name, status: "uploading", percent: 0 }]);
        try {
            const fileHash = await computeFileMD5(fileObj);
            const ext = fileObj.name.split(".").pop();
            const objectName = (isVideo ? "videos/" : "images/") + fileHash + "." + ext;
            const res = await apiClient.sessionurl(objectName, fileObj.type);
            const data = await res.data;
            // XHR for real progress — video caps at 85% to leave room for thumbnail step
            await xhrUpload(data.uploadUrl, fileObj, fileObj.type, pct =>
                setFileList(prev => prev.map(f => f.uid === fileObj.uid
                    ? { ...f, percent: isVideo ? Math.round(pct * 0.85) : pct }
                    : f
                ))
            );
            if (isVideo && data.uploadThumbUrl) {
                setFileList(prev => prev.map(f => f.uid === fileObj.uid ? { ...f, percent: 90 } : f));
                const video = document.createElement("video");
                video.src = URL.createObjectURL(fileObj);
                video.currentTime = 1;
                video.muted = true;
                await new Promise(resolve => video.addEventListener("loadeddata", resolve));
                const canvas = document.createElement("canvas");
                canvas.width = video.videoWidth / 4;
                canvas.height = video.videoHeight / 4;
                canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
                const thumbBlob = await new Promise(resolve => canvas.toBlob(resolve, "image/png"));
                const thumbFile = new File([thumbBlob], fileObj.name + "-thumb.png", { type: "image/png" });
                await fetch(data.uploadThumbUrl, { method: "PUT", headers: { "Content-Type": "image/png" }, body: thumbFile });
                setFileList(prev => prev.map(f => f.uid === fileObj.uid
                    ? { uid: fileObj.uid, name: fileObj.name, url: data.publicUrl, thumbUrl: data.publicThumbUrl, status: "done", percent: 100 }
                    : f
                ));
            } else {
                setFileList(prev => prev.map(f => f.uid === fileObj.uid
                    ? { uid: fileObj.uid, name: fileObj.name, url: data.publicUrl, thumbUrl: undefined, status: "done", percent: 100 }
                    : f
                ));
            }
        } catch (error) {
            console.error("Upload failed:", error);
            setFileList(prev => prev.map(f => f.uid === fileObj.uid ? { ...f, status: "error" } : f));
        }
        return Upload.LIST_IGNORE;
    };

    const handleRemove = (file) => {
        setFileList(prev => prev.filter(item => item.uid !== file.uid));
        return Upload.LIST_IGNORE;
    };

    // Update the submit button label every second while a cooldown is active.
    // Also disables the button if geolocation is not yet available.
    //
    // Reads usr.adc_ts directly from LoginContext (not local state) so that
    // the countdown is always in sync with the backend value — including after
    // logout/login, where the backend re-sends the unexpired adc_ts.
    // usr.adc_ts is also persisted to localStorage by App.js, so it survives
    // page refreshes and the countdown resumes automatically on next visit.
    useEffect(() => {
        if (!usr.token) return;

        // Require geolocation — coordinates are attached to each submission
        if (!loc.lat || !loc.lon) {
            setButtonText("Enable Location to Submit");
            setButtonDisabled(true);
            return;
        }

        // Tick the ADC countdown every second against usr.adc_ts
        const updateCountdown = () => {
            const text = getCountdown(usr.adc_ts);
            setButtonText(text);
            // Button is only active when the countdown message is the ready state
            setButtonDisabled(text !== "Feel free to Submit, if not Found");
        };
        updateCountdown();
        const intervalId = setInterval(updateCountdown, 1000);

        // Clear the interval when the component unmounts or dependencies change
        return () => clearInterval(intervalId);
    }, [usr.token, usr.adc_ts, loc.lat, loc.lon]);

    /**
     * openModal — fetches full post detail via the public /read endpoint
     * and displays a read-only verdict modal using Ant Design's imperative API.
     * Only called for non-BLANK posts (BLANK tags are not clickable).
     *
     * @param {string} addr — '<index>/<id>' post address.
     */
    const openModal = (addr) => {
        apiClient.readPost(addr)
            .then(response => {
                if (!response.data.hasOwnProperty("lbl")) return;
                const post = response.data;
                const cfg = MODAL_CFG[post.lbl] || MODAL_CFG.BLANK;
                // Use Ant Design imperative modal — no state management needed for read-only views
                modalApi[cfg.method]({
                    title: cfg.title,
                    width: "95%",
                    centered: true,
                    content: (
                        <div>
                            <Text strong>Claim:</Text>
                            <p style={{ paddingLeft: 40, paddingRight: 40 }}><i>{post.msg}</i></p>
                            <Text strong>Verdict:</Text>
                            <p style={{ paddingLeft: 40 }}>
                                <Tag color={LBL_COLOR[post.lbl] || "default"}>{post.lbl}</Tag>
                            </p>
                            <Text strong>Fact:</Text>
                            <div style={{ paddingLeft: 40, paddingRight: 40, marginTop: 8 }}>
                                <Markdown>{post.exp}</Markdown>
                            </div>
                        </div>
                    ),
                    footer: (_, { OkBtn }) => <OkBtn />,
                });
            })
            .catch(error => console.error("Error fetching post:", error));
    };

    /**
     * getSimilar — searches for posts similar to the current textarea value.
     * Called by the Search button and also after a successful submission to
     * refresh results.
     */
    const getSimilar = () => {
        setSearching(true);
        apiClient.searchPosts(querystr)
            .then(response => {
                setQueryres(response.data.hasOwnProperty("results") ? response.data.results : []);
            })
            .catch(error => console.error("Error searching:", error))
            .finally(() => setSearching(false));
    };

    /**
     * onSubmit — validates and submits the current textarea content.
     *
     * Pre-checks:
     *   1. Rejects if the exact message is already in the current search results
     *      (prevents trivial duplicates without a round-trip).
     *
     * Backend response handling:
     *   - SPAM match  → error toast, no cooldown started.
     *   - adc_ts set  → cooldown timer starts; button shows countdown.
     *   - Otherwise   → success toast, results refreshed.
     */
    const onSubmit = () => {
        if (!usr.adc_ts) {
            messageApi.error("Session expired. Please log in again.");
            return;
        }
        // Reject empty or whitespace-only submissions
        if (!querystr || !querystr.trim()) {
            messageApi.error("Cannot submit an empty message");
            return;
        }
        // Client-side duplicate check against current results
        if (queryres.some(elem => elem.msg === querystr)) {
            messageApi.error("Message Already Exists, Cannot Re-Submit");
            return;
        }
        apiClient.submitPost(querystr, [loc.lon, loc.lat], fileList)
            .then(response => {
                getSimilar();
                const data = response.data;
                if (data.total === 1 && ["SPAM"].includes(data.results?.[0]?.lbl)) {
                    messageApi.error("Can't Submit, Message Already Marked as Spam");
                } else if (data.adc_ts) {
                    setUsr(prev => ({ ...prev, adc_ts: data.adc_ts }));
                    if (data.total !== 0) setFileList([]);
                    messageApi.success(getCountdown(data.adc_ts));
                } else {
                    messageApi.error("Submission failed. Please try again.");
                }
            })
            .catch(error => {
                if (error.response?.status === 401) {
                    messageApi.error("Session expired. Please log in again.");
                } else {
                    messageApi.error("Submission failed. Please try again.");
                }
            });
    };

    return (
        <div className="page-container">
            {messageHolder}
            {modalHolder}

            <Row gutter={[16, 16]}>
                {/* ----------------------------------------------------------------
                    Left panel — TextArea + action buttons
                ---------------------------------------------------------------- */}
                <Col xs={24} md={8}>
                    <div style={{ display: "flex", flexDirection: "column", height: "85vh", gap: 16 }}>

                        {/* State selector — pin button auto-fills from login location */}
                        <div style={{ display: "flex", justifyContent: "center" }}>
                            <Space.Compact style={{ width: "100%", maxWidth: 400 }}>
                                <Button
                                    icon={<EnvironmentOutlined />}
                                    title="Auto-fill from login location"
                                    disabled={!usr.token}
                                    onClick={() => {
                                        if (usr.state) {
                                            setSelectedState(usr.state);
                                            localStorage.setItem('fnma_search_state', JSON.stringify(usr.state));
                                        } else {
                                            messageApi.info('Location unavailable — select a state manually');
                                        }
                                    }}
                                />
                                <Select
                                    value={selectedState}
                                    style={{ width: "100%", textAlign: "center" }}
                                    dropdownStyle={{ textAlign: "center" }}
                                    className="state-select-centered"
                                    placeholder="Select state"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={(usr.states || []).map(s => ({ value: s, label: s }))}
                                    onChange={value => {
                                        const v = value ?? null;
                                        setSelectedState(v);
                                        localStorage.setItem('fnma_search_state', JSON.stringify(v));
                                    }}
                                />
                                <Button icon={<EnvironmentOutlined />} style={{ visibility: "hidden" }} tabIndex={-1} />
                            </Space.Compact>
                        </div>

                        {/* Channel eligibility row — shown once state is selected and API responds */}
                        {channelStats && (() => {
                            const monthLabel = (n) => {
                                const d = new Date();
                                d.setDate(1);
                                d.setMonth(d.getMonth() - n);
                                return d.toLocaleString('en', { month: 'short' }) + '-' + String(d.getFullYear()).slice(2);
                            };
                            return (
                                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, fontSize: 13 }}>
                                    {channelStats.allowed
                                        ? <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 16 }} />
                                        : <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: 16 }} />}
                                    <span style={{ color: "#8c8c8c" }}>{monthLabel(3)}: <b>{channelStats.m3_pct}%</b></span>
                                    <span style={{ color: "#8c8c8c" }}>{monthLabel(2)}: <b>{channelStats.m2_pct}%</b></span>
                                    <span style={{ color: "#8c8c8c" }}>{monthLabel(1)}: <b>{channelStats.m1_pct}%</b></span>
                                    <span style={{ color: "#8c8c8c" }}>ADC: <b>{Math.round(usr.adc_rank ?? 0)}</b></span>
                                </div>
                            );
                        })()}

                        <TextArea
                            showCount
                            maxLength={10000}
                            value={querystr}
                            onChange={e => { setQuerystr(e.target.value); localStorage.setItem('fnma_search_query', e.target.value); }}
                            placeholder="Enter text to search or submit…"
                            style={{ flex: 1, resize: "none" }}
                        />
                        {/* Claim attachments — only shown when logged in */}
                        {usr.token && (
                            <div style={{ display: "flex", justifyContent: "center" }}>
                            <div className="compact-upload">
                                <Upload
                                    listType="picture-card"
                                    fileList={fileList}
                                    showUploadList={{ showPreviewIcon: false, showRemoveIcon: true }}
                                    customRequest={handleUpload}
                                    onRemove={handleRemove}
                                >
                                    {fileList.length >= 4 ? null : (
                                        <button style={{ border: 0, background: "none" }} type="button">
                                            <PlusOutlined />
                                            <div style={{ marginTop: 4, fontSize: 11 }}>Attach</div>
                                        </button>
                                    )}
                                </Upload>
                            </div>
                            </div>
                        )}
                        <Space direction="vertical" size={8} style={{ width: "100%" }}>
                            {/* Search — public endpoint, works without login */}
                            <Button block type="default" icon={<SearchOutlined />} shape="round" onClick={getSimilar} disabled={!querystr || !querystr.trim()}>
                                Search
                            </Button>
                            {/* Submit — disabled while cooldown is active, location unavailable, or field is empty */}
                            <Button
                                block
                                type="primary"
                                icon={<SendOutlined />}
                                shape="round"
                                onClick={onSubmit}
                                disabled={buttonDisabled || !querystr || !querystr.trim() || !selectedState}
                            >
                                {buttonText}
                            </Button>
                        </Space>
                    </div>
                </Col>

                {/* ----------------------------------------------------------------
                    Right panel — Search results list
                    BLANK posts are blurred (unverified content hidden from view).
                    Non-BLANK tags are clickable and open the verdict modal.
                ---------------------------------------------------------------- */}
                <Col xs={24} md={16}>
                    <Spin spinning={searching} tip="Searching…">
                    <InfiniteScroll
                        style={{ height: "80vh" }}
                        dataLength={queryres.length}
                        scrollableTarget="scrollableDiv"
                    >
                        <List
                            dataSource={queryres}
                            locale={{ emptyText: <span style={{ color: "#8c8c8c", display: "block", padding: "40px 0", textAlign: "center" }}>Search to see similar posts</span> }}
                            renderItem={item => {
                                // BLANK posts are blurred unless the exact text matches the query
                                // (the user can read their own just-submitted post)
                                const isBlank = item.lbl === "BLANK" && item.msg !== querystr;
                                return (
                                    <List.Item
                                        key={item.addr}
                                        style={{ padding: "12px 16px", alignItems: "flex-start" }}
                                    >
                                        <div style={{ flex: 1, minWidth: 0, marginRight: 16 }}>
                                            <pre style={{
                                                whiteSpace: "pre-line", wordBreak: "break-word",
                                                overflowWrap: "break-word", margin: 0,
                                                fontFamily: "inherit", fontSize: 14,
                                                // Blur unverified BLANK content; disable text selection
                                                filter: isBlank ? "blur(5px)" : "none",
                                                userSelect: isBlank ? "none" : "auto",
                                            }}>
                                                {item.msg}
                                            </pre>
                                        </div>
                                        {/* Verdict tag — clickable for non-BLANK to open modal */}
                                        <Tag
                                            color={LBL_COLOR[item.lbl] || "default"}
                                            style={{
                                                flexShrink: 0,
                                                cursor: item.lbl === "REAL" || item.lbl === "FAKE" ? "pointer" : "default",
                                            }}
                                            onClick={item.lbl === "REAL" || item.lbl === "FAKE" ? () => openModal(item.addr) : undefined}
                                        >
                                            {item.lbl}
                                        </Tag>
                                    </List.Item>
                                );
                            }}
                        />
                    </InfiniteScroll>
                    </Spin>
                </Col>
            </Row>
        </div>
    );
};
